import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Card,
  CardContent,
  Tooltip,
  InputAdornment,
  CircularProgress,
  FormControlLabel,
  Switch,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Home as HomeIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Block as BlockIcon,
  CheckCircle as ActiveIcon,
  Apartment as ApartmentIcon,
  Business as OfficeIcon,
  House as HouseIcon,
  Landscape as LandIcon,
} from '@mui/icons-material';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip as RechartsTooltip, Legend } from 'recharts';
import { useApp } from '../App';
import { realEstateApi, beneficiariesApi, jurisdictionsApi, holdersApi } from '../services/api';

const PROPERTY_TYPES = [
  { value: 'APARTMENT', label: 'Квартира', icon: <ApartmentIcon /> },
  { value: 'HOUSE', label: 'Дом', icon: <HouseIcon /> },
  { value: 'OFFICE', label: 'Офис', icon: <OfficeIcon /> },
  { value: 'RETAIL', label: 'Торговое помещение', icon: <OfficeIcon /> },
  { value: 'WAREHOUSE', label: 'Склад', icon: <OfficeIcon /> },
  { value: 'LAND', label: 'Земельный участок', icon: <LandIcon /> },
  { value: 'VILLA', label: 'Вилла', icon: <HouseIcon /> },
  { value: 'PARKING', label: 'Парковочное место', icon: <OfficeIcon /> },
  { value: 'OTHER', label: 'Другое', icon: <HomeIcon /> },
];

const CURRENCIES = ['USD', 'EUR', 'RUB', 'GBP', 'CHF', 'AED', 'CNY'];

const COLORS = ['#1B365D', '#C5A572', '#2E7D32', '#1565C0', '#6A1B9A', '#C62828', '#EF6C00', '#00838F'];

const formatCurrency = (amount, currency = 'USD') => {
  return new Intl.NumberFormat('ru-RU', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount || 0);
};

export default function RealEstate() {
  const { showNotification } = useApp();
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [properties, setProperties] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [beneficiaries, setBeneficiaries] = useState([]);
  const [jurisdictions, setJurisdictions] = useState([]);
  const [holders, setHolders] = useState([]);
  const [summary, setSummary] = useState(null);

  const [form, setForm] = useState({
    code: '',
    name: '',
    propertyType: 'APARTMENT',
    address: '',
    city: '',
    area: '',
    rooms: '',
    floor: '',
    yearBuilt: '',
    purchaseDate: '',
    purchasePrice: '',
    purchaseCurrency: 'USD',
    currentValue: '',
    valueCurrency: 'USD',
    valuationDate: '',
    rentalIncome: '',
    rentalCurrency: 'USD',
    isRented: false,
    tenantInfo: '',
    mortgageBalance: '',
    mortgageBank: '',
    mortgageRate: '',
    cadastralNumber: '',
    registrationNumber: '',
    insurancePolicy: '',
    insuranceExpiry: '',
    managementCompany: '',
    monthlyExpenses: '',
    notes: '',
    beneficiaryId: '',
    jurisdictionId: '',
    holderId: '',
  });

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [propertiesRes, benefRes, jurisRes, holdersRes, summaryRes] = await Promise.all([
        realEstateApi.getAll({ search }),
        beneficiariesApi.getAll({ isActive: true }),
        jurisdictionsApi.getAll({ isActive: true }),
        holdersApi.getAll({ isActive: true }),
        realEstateApi.getSummary(),
      ]);

      setProperties(propertiesRes.data);
      setBeneficiaries(benefRes.data);
      setJurisdictions(jurisRes.data);
      setHolders(holdersRes.data);
      setSummary(summaryRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
      showNotification('Ошибка загрузки данных', 'error');
    } finally {
      setLoading(false);
    }
  }, [search, showNotification]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleDialogOpen = (item = null) => {
    if (item) {
      setEditItem(item);
      setForm({
        code: item.code || '',
        name: item.name || '',
        propertyType: item.propertyType || 'APARTMENT',
        address: item.address || '',
        city: item.city || '',
        area: item.area || '',
        rooms: item.rooms || '',
        floor: item.floor || '',
        yearBuilt: item.yearBuilt || '',
        purchaseDate: item.purchaseDate || '',
        purchasePrice: item.purchasePrice || '',
        purchaseCurrency: item.purchaseCurrency || 'USD',
        currentValue: item.currentValue || '',
        valueCurrency: item.valueCurrency || 'USD',
        valuationDate: item.valuationDate || '',
        rentalIncome: item.rentalIncome || '',
        rentalCurrency: item.rentalCurrency || 'USD',
        isRented: item.isRented || false,
        tenantInfo: item.tenantInfo || '',
        mortgageBalance: item.mortgageBalance || '',
        mortgageBank: item.mortgageBank || '',
        mortgageRate: item.mortgageRate || '',
        cadastralNumber: item.cadastralNumber || '',
        registrationNumber: item.registrationNumber || '',
        insurancePolicy: item.insurancePolicy || '',
        insuranceExpiry: item.insuranceExpiry || '',
        managementCompany: item.managementCompany || '',
        monthlyExpenses: item.monthlyExpenses || '',
        notes: item.notes || '',
        beneficiaryId: item.beneficiaryId || '',
        jurisdictionId: item.jurisdictionId || '',
        holderId: item.holderId || '',
      });
    } else {
      setEditItem(null);
      setForm({
        code: '',
        name: '',
        propertyType: 'APARTMENT',
        address: '',
        city: '',
        area: '',
        rooms: '',
        floor: '',
        yearBuilt: '',
        purchaseDate: '',
        purchasePrice: '',
        purchaseCurrency: 'USD',
        currentValue: '',
        valueCurrency: 'USD',
        valuationDate: '',
        rentalIncome: '',
        rentalCurrency: 'USD',
        isRented: false,
        tenantInfo: '',
        mortgageBalance: '',
        mortgageBank: '',
        mortgageRate: '',
        cadastralNumber: '',
        registrationNumber: '',
        insurancePolicy: '',
        insuranceExpiry: '',
        managementCompany: '',
        monthlyExpenses: '',
        notes: '',
        beneficiaryId: '',
        jurisdictionId: '',
        holderId: '',
      });
    }
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    try {
      if (editItem) {
        await realEstateApi.update(editItem.id, form);
        showNotification('Объект обновлён', 'success');
      } else {
        await realEstateApi.create(form);
        showNotification('Объект создан', 'success');
      }
      setDialogOpen(false);
      loadData();
    } catch (error) {
      console.error('Error saving property:', error);
      showNotification(error.response?.data?.error || 'Ошибка сохранения', 'error');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Удалить этот объект недвижимости?')) return;
    try {
      await realEstateApi.delete(id);
      showNotification('Объект удалён', 'success');
      loadData();
    } catch (error) {
      console.error('Error deleting property:', error);
      showNotification('Ошибка удаления', 'error');
    }
  };

  const handleToggleActive = async (item) => {
    try {
      if (item.isActive) {
        await realEstateApi.deactivate(item.id);
        showNotification('Объект деактивирован', 'info');
      } else {
        await realEstateApi.activate(item.id);
        showNotification('Объект активирован', 'success');
      }
      loadData();
    } catch (error) {
      console.error('Error toggling status:', error);
      showNotification('Ошибка изменения статуса', 'error');
    }
  };

  // Chart data
  const typeChartData = summary?.byType
    ? Object.entries(summary.byType).map(([type, data]) => ({
        name: PROPERTY_TYPES.find(t => t.value === type)?.label || type,
        value: data.value,
        count: data.count,
      }))
    : [];

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 600 }}>
          Недвижимость
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <TextField
            size="small"
            placeholder="Поиск..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          <IconButton onClick={loadData}>
            <RefreshIcon />
          </IconButton>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleDialogOpen()}>
            Добавить
          </Button>
        </Box>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={3}>
          <Card sx={{ bgcolor: 'primary.main', color: 'white' }}>
            <CardContent>
              <Typography variant="overline">Всего объектов</Typography>
              <Typography variant="h4">{summary?.totalProperties || 0}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card sx={{ bgcolor: 'secondary.main', color: 'white' }}>
            <CardContent>
              <Typography variant="overline">Общая стоимость</Typography>
              <Typography variant="h5">{formatCurrency(summary?.totalValue)}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography variant="overline" color="text.secondary">Чистый капитал</Typography>
              <Typography variant="h5" color="success.main">
                {formatCurrency(summary?.netEquity)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Ипотека: {formatCurrency(summary?.totalMortgage)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography variant="overline" color="text.secondary">Арендный доход</Typography>
              <Typography variant="h5" color="info.main">
                {formatCurrency(summary?.totalRentalIncome)}/мес
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Сдаётся: {summary?.rentedCount || 0} объектов
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Chart and Table */}
      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2, height: 350 }}>
            <Typography variant="h6" gutterBottom>По типу</Typography>
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie
                  data={typeChartData}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  outerRadius={80}
                  label={({ name, count }) => `${name} (${count})`}
                >
                  {typeChartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <RechartsTooltip formatter={(value) => formatCurrency(value)} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        <Grid item xs={12} md={8}>
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Код</TableCell>
                  <TableCell>Название</TableCell>
                  <TableCell>Тип</TableCell>
                  <TableCell>Город</TableCell>
                  <TableCell align="right">Площадь</TableCell>
                  <TableCell align="right">Стоимость</TableCell>
                  <TableCell>Бенефициар</TableCell>
                  <TableCell>Статус</TableCell>
                  <TableCell align="center">Действия</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {properties.map((item) => (
                  <TableRow key={item.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>{item.code}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{item.name}</Typography>
                      {item.address && (
                        <Typography variant="caption" color="text.secondary">{item.address}</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={PROPERTY_TYPES.find(t => t.value === item.propertyType)?.label || item.propertyType}
                      />
                    </TableCell>
                    <TableCell>{item.city || '-'}</TableCell>
                    <TableCell align="right">{item.area ? `${item.area} м²` : '-'}</TableCell>
                    <TableCell align="right">
                      {formatCurrency(item.currentValue, item.valueCurrency)}
                    </TableCell>
                    <TableCell>{item.Beneficiary?.name || '-'}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        <Chip
                          size="small"
                          icon={item.isActive ? <ActiveIcon /> : <BlockIcon />}
                          label={item.isActive ? 'Активен' : 'Неактивен'}
                          color={item.isActive ? 'success' : 'default'}
                        />
                        {item.isRented && (
                          <Chip size="small" label="Сдаётся" color="info" />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="Редактировать">
                        <IconButton size="small" onClick={() => handleDialogOpen(item)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={item.isActive ? 'Деактивировать' : 'Активировать'}>
                        <IconButton size="small" onClick={() => handleToggleActive(item)}>
                          {item.isActive ? <BlockIcon fontSize="small" /> : <ActiveIcon fontSize="small" />}
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Удалить">
                        <IconButton size="small" onClick={() => handleDelete(item.id)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
                {properties.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={9} align="center">
                      <Typography color="text.secondary" sx={{ py: 3 }}>
                        Нет объектов недвижимости
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
      </Grid>

      {/* Edit/Create Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>
          {editItem ? 'Редактировать объект' : 'Добавить объект недвижимости'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {/* Basic Info */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="primary" gutterBottom>Основная информация</Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Код"
                value={form.code}
                onChange={(e) => setForm({ ...form, code: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} md={5}>
              <TextField
                fullWidth
                label="Название"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Тип объекта</InputLabel>
                <Select
                  value={form.propertyType}
                  label="Тип объекта"
                  onChange={(e) => setForm({ ...form, propertyType: e.target.value })}
                >
                  {PROPERTY_TYPES.map(t => (
                    <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={8}>
              <TextField
                fullWidth
                label="Адрес"
                value={form.address}
                onChange={(e) => setForm({ ...form, address: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Город"
                value={form.city}
                onChange={(e) => setForm({ ...form, city: e.target.value })}
              />
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField
                fullWidth
                type="number"
                label="Площадь (м²)"
                value={form.area}
                onChange={(e) => setForm({ ...form, area: e.target.value })}
              />
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField
                fullWidth
                type="number"
                label="Комнат"
                value={form.rooms}
                onChange={(e) => setForm({ ...form, rooms: e.target.value })}
              />
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField
                fullWidth
                label="Этаж"
                value={form.floor}
                onChange={(e) => setForm({ ...form, floor: e.target.value })}
              />
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField
                fullWidth
                type="number"
                label="Год постройки"
                value={form.yearBuilt}
                onChange={(e) => setForm({ ...form, yearBuilt: e.target.value })}
              />
            </Grid>

            {/* Ownership */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="primary" gutterBottom sx={{ mt: 1 }}>Владение</Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Бенефициар</InputLabel>
                <Select
                  value={form.beneficiaryId}
                  label="Бенефициар"
                  onChange={(e) => setForm({ ...form, beneficiaryId: e.target.value })}
                >
                  <MenuItem value="">Не указан</MenuItem>
                  {beneficiaries.map(b => (
                    <MenuItem key={b.id} value={b.id}>{b.name} ({b.code})</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Юрисдикция</InputLabel>
                <Select
                  value={form.jurisdictionId}
                  label="Юрисдикция"
                  onChange={(e) => setForm({ ...form, jurisdictionId: e.target.value })}
                >
                  <MenuItem value="">Не указана</MenuItem>
                  {jurisdictions.map(j => (
                    <MenuItem key={j.id} value={j.id}>{j.name} ({j.code})</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Через структуру</InputLabel>
                <Select
                  value={form.holderId}
                  label="Через структуру"
                  onChange={(e) => setForm({ ...form, holderId: e.target.value })}
                >
                  <MenuItem value="">Напрямую</MenuItem>
                  {holders.map(h => (
                    <MenuItem key={h.id} value={h.id}>{h.name} ({h.code})</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Value */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="primary" gutterBottom sx={{ mt: 1 }}>Стоимость</Typography>
            </Grid>
            <Grid item xs={6} md={3}>
              <TextField
                fullWidth
                type="number"
                label="Цена покупки"
                value={form.purchasePrice}
                onChange={(e) => setForm({ ...form, purchasePrice: e.target.value })}
              />
            </Grid>
            <Grid item xs={6} md={2}>
              <FormControl fullWidth>
                <InputLabel>Валюта</InputLabel>
                <Select
                  value={form.purchaseCurrency}
                  label="Валюта"
                  onChange={(e) => setForm({ ...form, purchaseCurrency: e.target.value })}
                >
                  {CURRENCIES.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                type="date"
                label="Дата покупки"
                value={form.purchaseDate}
                onChange={(e) => setForm({ ...form, purchaseDate: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <TextField
                fullWidth
                type="number"
                label="Текущая стоимость"
                value={form.currentValue}
                onChange={(e) => setForm({ ...form, currentValue: e.target.value })}
              />
            </Grid>
            <Grid item xs={6} md={2}>
              <FormControl fullWidth>
                <InputLabel>Валюта</InputLabel>
                <Select
                  value={form.valueCurrency}
                  label="Валюта"
                  onChange={(e) => setForm({ ...form, valueCurrency: e.target.value })}
                >
                  {CURRENCIES.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                type="date"
                label="Дата оценки"
                value={form.valuationDate}
                onChange={(e) => setForm({ ...form, valuationDate: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            {/* Rental */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="primary" gutterBottom sx={{ mt: 1 }}>Аренда</Typography>
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControlLabel
                control={
                  <Switch
                    checked={form.isRented}
                    onChange={(e) => setForm({ ...form, isRented: e.target.checked })}
                  />
                }
                label="Сдаётся"
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <TextField
                fullWidth
                type="number"
                label="Арендный доход/мес"
                value={form.rentalIncome}
                onChange={(e) => setForm({ ...form, rentalIncome: e.target.value })}
                disabled={!form.isRented}
              />
            </Grid>
            <Grid item xs={6} md={2}>
              <FormControl fullWidth disabled={!form.isRented}>
                <InputLabel>Валюта</InputLabel>
                <Select
                  value={form.rentalCurrency}
                  label="Валюта"
                  onChange={(e) => setForm({ ...form, rentalCurrency: e.target.value })}
                >
                  {CURRENCIES.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={5}>
              <TextField
                fullWidth
                label="Информация об арендаторе"
                value={form.tenantInfo}
                onChange={(e) => setForm({ ...form, tenantInfo: e.target.value })}
                disabled={!form.isRented}
              />
            </Grid>

            {/* Mortgage */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="primary" gutterBottom sx={{ mt: 1 }}>Ипотека</Typography>
            </Grid>
            <Grid item xs={6} md={3}>
              <TextField
                fullWidth
                type="number"
                label="Остаток ипотеки"
                value={form.mortgageBalance}
                onChange={(e) => setForm({ ...form, mortgageBalance: e.target.value })}
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <TextField
                fullWidth
                label="Банк"
                value={form.mortgageBank}
                onChange={(e) => setForm({ ...form, mortgageBank: e.target.value })}
              />
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField
                fullWidth
                type="number"
                label="Ставка (%)"
                value={form.mortgageRate}
                onChange={(e) => setForm({ ...form, mortgageRate: e.target.value })}
              />
            </Grid>
            <Grid item xs={6} md={4}>
              <TextField
                fullWidth
                type="number"
                label="Ежемесячные расходы"
                value={form.monthlyExpenses}
                onChange={(e) => setForm({ ...form, monthlyExpenses: e.target.value })}
              />
            </Grid>

            {/* Documents */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="primary" gutterBottom sx={{ mt: 1 }}>Документы</Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Кадастровый номер"
                value={form.cadastralNumber}
                onChange={(e) => setForm({ ...form, cadastralNumber: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Номер регистрации"
                value={form.registrationNumber}
                onChange={(e) => setForm({ ...form, registrationNumber: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Управляющая компания"
                value={form.managementCompany}
                onChange={(e) => setForm({ ...form, managementCompany: e.target.value })}
              />
            </Grid>
            <Grid item xs={6} md={4}>
              <TextField
                fullWidth
                label="Страховой полис"
                value={form.insurancePolicy}
                onChange={(e) => setForm({ ...form, insurancePolicy: e.target.value })}
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <TextField
                fullWidth
                type="date"
                label="Страховка до"
                value={form.insuranceExpiry}
                onChange={(e) => setForm({ ...form, insuranceExpiry: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={5}>
              <TextField
                fullWidth
                multiline
                rows={2}
                label="Примечания"
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Отмена</Button>
          <Button variant="contained" onClick={handleSubmit}>
            {editItem ? 'Сохранить' : 'Создать'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
